import { useEffect, useRef, useState } from 'react';

import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { Clear, Close, Send } from '@material-ui/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';

import { ReactComponent as ComentariosIcon } from 'assets/images/comentarios.svg';
import { ContactAvatar } from 'components/ContactAvatar';
import ImageViewer from 'components/ImageViewer';
import InfiniteScroll from 'components/InfiniteScroll';
import TagsV2 from 'components/TagsV2/TagsV2';
import useCurrentUser from 'hooks/useCurrentUser';
import { useAppDispatch, useAppSelector } from 'store';
import {
  atendimentosSelector,
  fetchAtendimentoContato,
  resetAtendimentoContato,
} from 'store/reducers/atendimentos';
import {
  addComentario,
  comentariosSelector,
  fetchComentarios,
  removerComentario,
  resetComentarios,
} from 'store/reducers/comentarios';
import getIconTipoCanal from 'utils/getIconTipoCanal';
import getUsuarioNomeExibicao from 'utils/getUsuarioNomeExibicao';

import DetailItem from './DetailItems';
import TabPanelAtendimentoSkeleton from './TabPanelAtendimentoSkeleton';

const useStyles = makeStyles(
  ({ palette, spacing, transitions, typography }) => ({
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    headingSection: {
      fontWeight: typography.fontWeightBold as number,
      color: alpha(palette.primary.main, 0.7),
      marginBlock: spacing(2),
    },
    list: {
      height: '100%',
      position: 'relative',
      overflow: 'hidden auto',
    },
    detail: {
      '& > .MuiTypography-root': {
        fontSize: 14,
        width: 100,
      },

      '& > div > span': {
        fontSize: 14,
      },
    },
    tagsWrapper: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      marginTop: 4,
    },
    card: {
      width: '100%',
      overflow: 'hidden',
      backgroundColor: palette.background.tertiary,
      '& > .MuiCardHeader-root': {
        paddingBottom: 0,
      },
      '& > .MuiCardContent-root': {
        padding: spacing(0.5, 2),
        paddingBottom: spacing(1),
        wordBreak: 'break-word',
      },
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      borderTop: '1px solid',
      paddingTop: spacing(2),
      borderColor: palette.divider,
      gap: spacing(1),
    },
    underlineInput: {
      border: 0,
      borderRadius: spacing(3),
      backgroundColor: alpha(palette.action.disabledBackground, 0.05),
      color: palette.text.primary,
    },
    input: {
      fontSize: typography.pxToRem(14),
      borderRadius: spacing(3),
      paddingLeft: spacing(1),

      '& .MuiInputBase-input': {
        padding: spacing(1, 1, 1, 0),
      },
    },
    inputRoot: {
      paddingBlock: spacing(1.5),
    },
    button: {
      transform: 'scale(1, 1)',
      transition: transitions.create(['transform', 'color'], {
        duration: transitions.duration.shorter,
        easing: transitions.easing.easeInOut,
      }),
    },
    buttonHidden: {
      transform: 'scale(0, 0)',
      '& > $icon': {
        opacity: 0,
      },
    },
    emptyComentarios: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      overflow: 'hidden',
      paddingTop: spacing(12),
      gap: spacing(2),
    },
    icon: {
      transition: transitions.create(['opacity'], {
        duration: transitions.duration.shorter,
        easing: transitions.easing.easeInOut,
      }),
    },
  }),
);

interface TabPanelAtendimentoProps {
  contatoId: string;
  atendimentoId: string;
}

export default function TabPanelAtendimento({
  contatoId,
  atendimentoId,
}: TabPanelAtendimentoProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const currentUser = useCurrentUser();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const { atendimentoContato, loadingAtendimentoContato } =
    useAppSelector(atendimentosSelector);
  const {
    comentarios,
    comentariosCursor,
    loadingComentarios,
    loadingRemoverComentario,
    loadingAddComentario,
  } = useAppSelector(comentariosSelector);

  const onfocus = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  const handleAdd = () => {
    if (atendimentoId && !loadingAddComentario) {
      const texto = inputValue.trim();
      if (texto) {
        /**
         * Processos cruciais realizados após iniciar um novo atendimento da fila de espera
         *
         * 1º Limpa o estado do componente
         * 2º Retoma o foco no input
         * 3º Recarrega os comentários para atualizá-los
         */
        dispatch(addComentario({ atendimentoId, texto }))
          .unwrap()
          .then(() => {
            setInputValue('');
            onfocus();

            dispatch(resetComentarios());
            dispatch(fetchComentarios({ atendimentoId }));
          })
          .catch(error => {
            if (error.response) enqueueSnackbar(error.response.data.message);
          });
      }
    }
  };

  const handleConfirmRemove = (comentarioId: string) => {
    confirm({
      title: <Typography variant="body2">Remover comentário</Typography>,
      description: (
        <Typography variant="caption">
          Deseja mesmo remover este comentário permanentemente?
        </Typography>
      ),
      cancellationText: 'Cancelar',
      confirmationText: 'Remover',
      confirmationButtonProps: { variant: 'contained', color: 'secondary' },
      dialogProps: { maxWidth: 'xs' },
    })
      .then(async () => {
        if (atendimentoId) {
          /**
           * Remove um comentario
           * Limpa e atualiza a lista de comentarios após a remoção.
           */
          await dispatch(removerComentario({ atendimentoId, comentarioId }))
            .unwrap()
            .then(() => {
              onfocus();

              dispatch(resetComentarios());
              dispatch(fetchComentarios({ atendimentoId }));
            })
            .catch(error => {
              if (error.response) enqueueSnackbar(error.response.data.message);
            });
        }
      })
      .catch(() => {
        //
      });
  };

  useEffect(() => {
    if (atendimentoId) {
      dispatch(fetchComentarios({ atendimentoId }));
      dispatch(fetchAtendimentoContato({ contatoId, atendimentoId }));
    }
    return () => {
      dispatch(resetComentarios());
      dispatch(resetAtendimentoContato());
    };
  }, [atendimentoId, contatoId, dispatch]);

  const onLoadNextComentarios = () => {
    const { afterCursor } = comentariosCursor;
    if (atendimentoId && afterCursor) {
      dispatch(
        fetchComentarios({
          atendimentoId,
          cursor: { afterCursor },
        }),
      );
    }
  };

  const renderComentarios = comentarios.map(comentario => {
    const { id, usuario, criadoEm, texto } = comentario;
    const fromMe = currentUser?.id === usuario.id;
    const autor = fromMe ? 'Você' : getUsuarioNomeExibicao(usuario);

    return (
      <ListItem key={id} disableGutters>
        <Card className={classes.card} elevation={0}>
          <CardHeader
            style={{ paddingTop: 6, paddingLeft: 6, paddingRight: 6 }}
            title={autor}
            titleTypographyProps={{ noWrap: true, variant: 'caption' }}
            subheader={dayjs(criadoEm).fromNow()}
            subheaderTypographyProps={{ variant: 'caption' }}
            avatar={
              <ImageViewer src={usuario.foto}>
                <ContactAvatar
                  src={usuario.foto}
                  style={{ width: 40, height: 40 }}
                />
              </ImageViewer>
            }
            action={
              fromMe && (
                <IconButton
                  aria-label="remove"
                  onClick={() => handleConfirmRemove(id)}
                  disabled={loadingRemoverComentario}
                >
                  {loadingRemoverComentario ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <Close fontSize="small" />
                  )}
                </IconButton>
              )
            }
          />

          <CardContent>
            <Typography variant="caption" color="textSecondary" component="p">
              {texto}
            </Typography>
          </CardContent>
        </Card>
      </ListItem>
    );
  });

  if (loadingAtendimentoContato) return <TabPanelAtendimentoSkeleton />;

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box display="flex" flexDirection="column" gridGap={4}>
        <DetailItem
          layout="row"
          isContactItem
          label="Código"
          value={`${atendimentoContato?.code}`}
        />
        <DetailItem
          layout="row"
          isContactItem
          label="Iniciado"
          value={dayjs(atendimentoContato?.startedIn).format(
            'DD/MM/YY[, às] HH:mm',
          )}
        />
        <DetailItem
          layout="row"
          isContactItem
          label="Canal"
          value={
            <Box display="flex" alignItems="center" gridGap={8}>
              {atendimentoContato?.channel?.type &&
                getIconTipoCanal({
                  tipoCanal: atendimentoContato?.channel?.type,
                  size: 16,
                  isModoHibrido: atendimentoContato?.channel?.isHybridMode,
                })}
              <span style={{ marginTop: 2 }}>
                {atendimentoContato?.channel?.name}
              </span>
            </Box>
          }
        />
        <DetailItem
          layout="row"
          isContactItem
          label="Equipe"
          value={atendimentoContato?.team?.name}
        />

        <DetailItem
          layout="row"
          isContactItem
          label="Tags"
          value={
            <TagsV2
              tags={
                atendimentoContato?.tags?.map(t => ({
                  id: t.id,
                  nome: t.name,
                  cor: t.color,
                })) || []
              }
              id={atendimentoId}
            />
          }
        />
      </Box>

      <Typography variant="body2" className={classes.headingSection}>
        Comentários do atendimento
      </Typography>

      <List id="listComentarios" className={classes.list} disablePadding dense>
        {!loadingComentarios && comentarios.length === 0 && (
          <Box className={classes.emptyComentarios}>
            <ComentariosIcon />
            <Typography variant="body2" color="textSecondary">
              Não há comentários neste atendimento.
            </Typography>
          </Box>
        )}

        <InfiniteScroll
          data={comentarios}
          next={onLoadNextComentarios}
          hasMore={Boolean(comentariosCursor.afterCursor)}
          loading={loadingComentarios}
          scrollableTarget="listComentarios"
        >
          {renderComentarios}
        </InfiniteScroll>
      </List>

      <Box component="footer" className={classes.footer}>
        <ContactAvatar src={currentUser?.foto} />

        <TextField
          inputRef={inputRef}
          autoFocus
          fullWidth
          multiline
          maxRows={2}
          placeholder="Escreva um comentário"
          value={inputValue}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setInputValue(event.target.value)
          }
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              handleAdd();
            }
          }}
          variant="outlined"
          InputProps={{
            classes: {
              notchedOutline: classes.underlineInput,
              root: classes.inputRoot,
            },
            endAdornment: (
              <IconButton
                size="small"
                onClick={() => setInputValue('')}
                className={clsx(classes.button, {
                  [classes.buttonHidden]: inputValue === '',
                })}
              >
                <Clear fontSize="small" />
              </IconButton>
            ),
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          inputProps={{ maxLength: 255, className: classes.input }}
        />

        <IconButton disabled={!inputValue} onClick={handleAdd}>
          <Send color={inputValue ? 'primary' : 'disabled'} />
        </IconButton>
      </Box>
    </Box>
  );
}
